import React, { useCallback, useEffect, useState } from "react";
import {
  getBalanceHistory,
  getClientMe,
  getTablets,
  isBasicConfigured,
} from "../api/protected";
import BalanceTopUpModal from "../components/BalanceTopUpModal";
import TabletInfoModal from "../components/TabletInfoModal";
import TariffsModal from "../components/TariffsModal";
import { PageLoader } from "../components/Loader";
import { asArray } from "../poster/response";
import { getAccountUrl } from "../session";
import { apiAmountToSom, formatSomNumber } from "../utils/apiMoney";
import {
  BALANCE_HISTORY_PAGE_SIZE,
  balanceHistoryRowTone,
  balanceHistoryStatusBadge,
  buildTabletIdToLabelMap,
  formatBalanceChange,
  formatBalanceHistoryDate,
  labelBalanceHistoryReason,
  labelBalanceHistoryType,
  labelUnknownBalanceReason,
  parseBalanceHistoryPayload,
} from "../utils/balanceHistoryDisplay";

function WalletIcon({ size = 20 }) {
  const c = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round",
    strokeLinejoin: "round",
  };
  return (
    <svg {...c} aria-hidden>
      <path d="M20 12V8H6a2 2 0 0 1 0-4h12v4" />
      <path d="M4 6v12a2 2 0 0 0 2 2h14v-4" />
      <path d="M18 12a2 2 0 0 0 0 4h4v-4z" />
    </svg>
  );
}

function ChevronIcon({ dir, size = 18 }) {
  const pts =
    dir === "left" ? "15 18 9 12 15 6" : "9 18 15 12 9 6";
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points={pts} />
    </svg>
  );
}

export default function BillingPage() {
  const accountUrl = (getAccountUrl() || "").trim();
  const [items, setItems] = useState(/** @type {any[]} */ ([]));
  const [meta, setMeta] = useState(/** @type {any} */ (null));
  const [balance, setBalance] = useState(/** @type {number | null} */ (null));
  const [tabletMap, setTabletMap] = useState(
    /** @type {Map<string, string>} */ (new Map()),
  );
  const [tabletRecords, setTabletRecords] = useState(
    /** @type {Map<string, Record<string, unknown>>} */ (new Map()),
  );
  const [load, setLoad] = useState(true);
  const [err, setErr] = useState(/** @type {string | null} */ (null));
  const [topUpOpen, setTopUpOpen] = useState(false);
  const [tariffsOpen, setTariffsOpen] = useState(false);
  const [tabletInfo, setTabletInfo] = useState(
    /** @type {Record<string, unknown> | null} */ (null),
  );

  const loadAll = useCallback(
    async (requestedPage = 1) => {
      if (!isBasicConfigured() || !accountUrl) {
        setItems([]);
        setMeta(null);
        setBalance(null);
        setTabletMap(new Map());
        setTabletRecords(new Map());
        setErr(null);
        setLoad(false);
        return;
      }
      const page = Math.max(1, Math.floor(requestedPage) || 1);
      setLoad(true);
      setErr(null);
      try {
        const [meRaw, histRaw, tabsRaw] = await Promise.all([
          getClientMe(accountUrl),
          getBalanceHistory(accountUrl, {
            page,
            limit: BALANCE_HISTORY_PAGE_SIZE,
          }),
          getTablets(accountUrl).catch(() => []),
        ]);
        const me =
          meRaw && typeof meRaw === "object"
            ? /** @type {Record<string, unknown>} */ (meRaw)
            : null;
        setBalance(
          me?.balance != null && me.balance !== ""
            ? apiAmountToSom(me.balance)
            : null,
        );
        const parsed = parseBalanceHistoryPayload(histRaw);
        setItems(parsed.items);
        setMeta(parsed.meta);
        const tabsArr = asArray(tabsRaw);
        setTabletMap(buildTabletIdToLabelMap(tabsArr));
        const recMap = new Map();
        for (const t of tabsArr) {
          if (!t || typeof t !== "object") continue;
          const r = /** @type {Record<string, unknown>} */ (t);
          const id = r._id != null ? String(r._id) : "";
          const tid = r.tablet_id != null ? String(r.tablet_id) : "";
          if (id) recMap.set(id, r);
          if (tid) recMap.set(tid, r);
        }
        setTabletRecords(recMap);
      } catch (e) {
        const status = /** @type {any} */ (e)?.response?.status;
        let msg =
          /** @type {any} */ (e)?.message || "Не удалось загрузить историю";
        if (status === 404) {
          msg = "Клиент с таким account_url не найден";
        } else if (status === 400) {
          msg = "Не указан account_url или неверные page/limit";
        } else if (status === 401) {
          msg = "Неверная авторизация API";
        }
        setErr(String(msg));
        setItems([]);
        setMeta(null);
        setBalance(null);
      } finally {
        setLoad(false);
      }
    },
    [accountUrl],
  );

  useEffect(() => {
    void loadAll(1);
  }, [loadAll]);

  const canUseApi = isBasicConfigured() && accountUrl;
  const totalCount = meta?.total ?? 0;
  const currentPage = meta?.page ?? 1;
  const totalPages = meta?.totalPages ?? 0;
  /** Страница из meta или минимум 1, если есть записи (на случай неконсистентного meta). */
  const safeTotalPages =
    totalCount > 0 ? Math.max(totalPages, 1) : 0;

  const goPrev = () => {
    if (currentPage <= 1 || load) {
      return;
    }
    void loadAll(currentPage - 1);
  };

  const goNext = () => {
    if (
      safeTotalPages <= 0 ||
      currentPage >= safeTotalPages ||
      load
    ) {
      return;
    }
    void loadAll(currentPage + 1);
  };

  if (load && canUseApi) {
    return (
      <section className="bodyContainer bodyContainer--billing billingPage">
        <h2 className="settingsPage__title billingPage__title">Биллинг</h2>
        <PageLoader label="Загрузка" />
      </section>
    );
  }

  return (
    <section className="bodyContainer bodyContainer--billing billingPage">
      <div className="billingPage__header">
        <div className="billingPage__headerMain">
          <h2 className="settingsPage__title billingPage__title">Биллинг</h2>
          <div className="billingPage__toolbar">
            <div className="billingPage__balanceGroup">
              <div
                className={[
                  "billingPage__balanceOutlined",
                  balance == null || Number.isNaN(balance)
                    ? "billingPage__balanceOutlined--muted"
                    : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                role="group"
                aria-label="Текущий баланс"
              >
                <span
                  className="billingPage__balanceOutlinedLabel"
                  id="billingBalanceLabel"
                >
                  Текущий баланс
                </span>
                {balance != null && !Number.isNaN(balance) ? (
                  <strong
                    className="billingPage__balanceValue"
                    aria-labelledby="billingBalanceLabel"
                  >
                    {formatSomNumber(balance)} сум
                  </strong>
                ) : (
                  <span className="billingPage__balanceValue billingPage__balanceValue--empty">
                    Баланс недоступен
                  </span>
                )}
              </div>
              <button
                type="button"
                className="billingPage__topUpBtn"
                onClick={() => setTopUpOpen(true)}
                disabled={!canUseApi}
                aria-label="Пополнить баланс"
                title={
                  !canUseApi
                    ? "Нужны API credentials и account_url"
                    : undefined
                }
              >
                <span className="billingPage__topUpBtnIcon">
                  <WalletIcon size={22} />
                </span>
                <span className="billingPage__topUpBtnText">
                  Пополнить баланс
                </span>
              </button>
            </div>
            <div className="billingPage__headerActions">
              <button
                type="button"
                className="link-button link-button--sm link-button--secondary billingPage__toolbarBtn"
                onClick={() => setTariffsOpen(true)}
              >
                Тарифы
              </button>
            </div>
          </div>
        </div>
      </div>

      <BalanceTopUpModal
        show={topUpOpen}
        onHide={() => setTopUpOpen(false)}
        accountUrl={accountUrl}
      />
      <TariffsModal
        show={tariffsOpen}
        onHide={() => setTariffsOpen(false)}
      />
      <TabletInfoModal
        show={tabletInfo != null}
        onHide={() => setTabletInfo(null)}
        tablet={tabletInfo}
      />

      {!canUseApi && (
        <p className="text-warning small">
          Укажите Basic Auth и войдите с аккаунтом, для которого сохранён{" "}
          <code>account_url</code>.
        </p>
      )}

      {err && canUseApi && (
        <div className="alert alert-danger small d-flex flex-wrap align-items-center gap-2">
          <span>{err}</span>
          <button
            type="button"
            className="link-button link-button--sm"
            onClick={() => void loadAll(currentPage)}
          >
            Повторить
          </button>
        </div>
      )}

      {canUseApi && !err && meta != null && totalCount === 0 && (
        <p className="text-muted billingPage__empty">Пока нет операций</p>
      )}

      {canUseApi && !err && meta != null && items.length > 0 && (
        <>
          <div className="billingPage__tableWrap">
            <table className="billingPage__table">
              <thead>
                <tr>
                  <th>Дата и время</th>
                  <th>Статус</th>
                  <th>Операция</th>
                  <th>Сумма</th>
                  <th>Касса</th>
                  <th>Примечание</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row, idx) => {
                  const r = /** @type {Record<string, unknown>} */ (row);
                  const id = String(r._id ?? idx);
                  const ch = formatBalanceChange(r.change);
                  const tone = balanceHistoryRowTone(r.type);
                  const badge = balanceHistoryStatusBadge(r.type);
                  const typeLabel = labelBalanceHistoryType(r.type);
                  const reasonHuman = labelBalanceHistoryReason(r.reason);
                  const reasonRaw =
                    r.reason != null && String(r.reason) !== ""
                      ? String(r.reason)
                      : "";
                  const reasonSecondary =
                    reasonHuman ||
                    (reasonRaw ? labelUnknownBalanceReason(reasonRaw) : "");
                  const tabletRaw = r.tablet_id;
                  const tabletPopulated =
                    tabletRaw && typeof tabletRaw === "object"
                      ? /** @type {Record<string, unknown>} */ (tabletRaw)
                      : null;
                  const tabletId = tabletPopulated
                    ? tabletPopulated._id != null
                      ? String(tabletPopulated._id)
                      : ""
                    : tabletRaw != null
                      ? String(tabletRaw)
                      : "";
                  const tabletLabel = tabletId
                    ? tabletMap.get(tabletId) || tabletId.slice(-8)
                    : "—";
                  const tabletForModal = tabletPopulated
                    || (tabletId ? tabletRecords.get(tabletId) || null : null);
                  const rowToneClass =
                    tone === "credit"
                      ? "billingPage__row--credit"
                      : tone === "debit" || tone === 'refund'
                        ? "billingPage__row--debit"
                              : "billingPage__row--neutral";
                  return (
                    <tr key={id} className={rowToneClass}>
                      <td className="billingPage__cell billingPage__cell--date">
                        {formatBalanceHistoryDate(r.createdAt)}
                      </td>
                      <td className="billingPage__cell">
                        <span
                          className={[
                            "billingPage__badge",
                            tone === "credit"
                              ? "billingPage__badge--credit"
                              : tone === "debit" || tone === 'refund'
                                ? "billingPage__badge--debit"
                                : "billingPage__badge--neutral",
                          ].join(" ")}
                        >
                          {badge}
                        </span>
                      </td>
                      <td className="billingPage__cell">{typeLabel}</td>
                      <td
                        className={[
                          "billingPage__cell billingPage__cell--amount",
                          tone === "credit"
                            ? "billingPage__amount--credit"
                            : "billingPage__amount--debit",
                        ].join(" ")}
                      >
                        {ch.text} сум
                      </td>
                      <td
                        className={[
                          "billingPage__cell billingPage__cell--muted",
                          tabletId ? "billingPage__cell--clickable" : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        onClick={
                          tabletId
                            ? () => setTabletInfo(tabletForModal || { _id: tabletId })
                            : undefined
                        }
                        onKeyDown={
                          tabletId
                            ? (e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  setTabletInfo(tabletForModal || { _id: tabletId });
                                }
                              }
                            : undefined
                        }
                        role={tabletId ? "button" : undefined}
                        tabIndex={tabletId ? 0 : undefined}
                        aria-label={tabletId ? "Подробнее о кассе" : undefined}
                      >
                        {tabletLabel}
                      </td>
                      <td className="billingPage__cell billingPage__cell--muted billingPage__cell--reason">
                        {reasonSecondary || "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <nav
            className="billingPage__pagination"
            aria-label="Страницы истории операций"
          >
            <button
              type="button"
              className="billingPage__pageBtn"
              disabled={
                currentPage <= 1 || load || safeTotalPages <= 1
              }
              onClick={goPrev}
              aria-label="Предыдущая страница"
              title="Предыдущая страница"
            >
              <ChevronIcon dir="left" />
            </button>
            <span className="billingPage__paginationInfo">
              Страница {currentPage} из {safeTotalPages}
            </span>
            <button
              type="button"
              className="billingPage__pageBtn"
              disabled={
                safeTotalPages <= 1 ||
                currentPage >= safeTotalPages ||
                load
              }
              onClick={goNext}
              aria-label="Следующая страница"
              title="Следующая страница"
            >
              <ChevronIcon dir="right" />
            </button>
          </nav>
        </>
      )}
    </section>
  );
}
